import { Button } from 'components/UI/Tailwind/Button';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { z } from 'zod';
import { RecommendationListItem } from './RecommendationListItem';
import { useNewRecommendationsContext } from './NewRecommendationsContext';
import {
  type PipelineCandidateSearchFilterSortResponse,
  CandidatePipelineStatus,
  RecommendationDraftMode,
} from 'client/types.gen';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getTeamPipelineCandidatesOptions,
  postTeamPipelineSubmissionMutation,
} from 'client/@tanstack/react-query.gen';
import { Dialog } from 'components/UI/Tailwind/Dialog';
import { useTranslation, Trans } from 'react-i18next';
import { RecommendationPanel } from './RecommendationPanel';
import { IconHeartHandshake } from 'components/UI/Icons/HeartHandshake';
import { useWizardFormNavigation } from 'helpers/form';
import { ListPlaceholderLoading } from 'pages/TeamCandidates/CandidateListPlaceholder/ListPlaceholderLoading';
import toast from 'react-hot-toast/headless';
import { schema as commentarySchema } from './FormCommentary';
import { FormProvider } from 'react-hook-form';
import { FormCheckbox } from 'components/UI/Tailwind/FormCheckbox';
import { InvoiceOverdueBanner } from 'components/InvoiceOverdueBanner';
import {
  getInvoiceStatusSeverity,
  isInvoiceOverdue,
} from 'helpers/invoiceStatus';
import { useIsPersonAdmin } from 'helpers/query';
import {
  TailoredHighlightsProvider,
  useTailoredHighlightDrafts,
  type TailoredHighlight,
} from './TailoredHighlights';

export const schema = z.object({
  skip_notifications: z.boolean(),
  candidates: z.record(z.string(), commentarySchema),
});

export type Schema = z.infer<typeof schema>;

// The text, the tick and the model's wording are sent; empty text is dropped.
const tailoredHighlightsPayload = (highlights?: TailoredHighlight[]) =>
  highlights
    ?.filter(({ highlight }) => highlight.trim())
    .map(({ highlight, is_selected, generated }) => ({
      highlight,
      is_selected,
      generated,
    }));

/**
 * Drafts the AI highlights for every candidate as step 2 opens and
 * shares them with the form below.
 */
export function Step2() {
  const {
    getSelectedCandidates,
    getSelectedCandidateUuids,
    pipelineId,
    teamId,
  } = useNewRecommendationsContext();
  const candidates = getSelectedCandidates();
  const candidateUuids = getSelectedCandidateUuids();

  // Skip candidates whose saved draft already has AI highlights; they can
  // still regenerate.
  const skipTailoredUuids = useMemo(
    () =>
      new Set(
        candidates
          .filter((c) => c.recommendation?.tailored_highlights?.length)
          .map((c) => c.uuid),
      ),
    [candidates],
  );
  const tailoredDrafts = useTailoredHighlightDrafts({
    teamUuid: teamId,
    pipelineUuid: pipelineId,
    candidateUuids,
    skipUuids: skipTailoredUuids,
  });

  return (
    <TailoredHighlightsProvider value={tailoredDrafts}>
      <Step2Form />
    </TailoredHighlightsProvider>
  );
}

function Step2Form() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { onPreviousStep } = useWizardFormNavigation();

  const [skipNotifications, setSkipNotifications] = useState(false);

  const {
    getSelectedCandidates,
    getSelectedCandidateUuids,
    pipelineId,
    teamId,
    clearSelectedCandidates,
    onExit,
  } = useNewRecommendationsContext();

  const queryPipeline = useQuery({
    ...getTeamPipelineCandidatesOptions({
      path: {
        pipeline_uuid: pipelineId,
        team_uuid: teamId,
      },
    }),
    select: (data) => data?.pipeline,
  });

  // For reference, the list of candidate objects selected in Step 1.
  const candidates = getSelectedCandidates();
  // The memoized list that matches our candidate objects, we'll use this
  // to query for off limits status.
  const candidateUuids = getSelectedCandidateUuids();

  const isAdmin = useIsPersonAdmin();
  const overdueCandidates = isAdmin
    ? candidates.filter((candidate) =>
        isInvoiceOverdue(candidate.invoice_status),
      )
    : [];
  const overdueNames = overdueCandidates
    .map((candidate) => candidate.name)
    .filter(Boolean);
  const overdueSeverity = overdueCandidates.some(
    (candidate) => getInvoiceStatusSeverity(candidate.invoice_status) === 'red',
  )
    ? ('red' as const)
    : ('amber' as const);
  // Which candidate is currently being edited.
  const [selectedCandidate, setSelectedCandidate] = useState<
    PipelineCandidateSearchFilterSortResponse | undefined
  >(candidates[0]);

  useEffect(() => {
    if (
      !candidates.find(
        (candidate) => candidate.uuid === selectedCandidate?.uuid,
      )
    ) {
      if (candidates.length > 0) {
        setSelectedCandidate(candidates[0]);
      } else {
        setSelectedCandidate(undefined);
      }
    }
  }, [candidates, selectedCandidate]);

  const methods = useForm<Schema>({
    defaultValues: {
      skip_notifications: false,
      candidates: {},
    },
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);

  const onCandidateClick = (
    candidate: PipelineCandidateSearchFilterSortResponse,
  ) => {
    setSelectedCandidate(candidate);
  };

  const postRecommendations = useMutation({
    ...postTeamPipelineSubmissionMutation(),
    onSuccess: async () => {
      clearSelectedCandidates();
      setIsPublishDialogOpen(false);
      onExit();

      queryClient.invalidateQueries();
    },
    onError: () => {
      toast.error(t('error.generic'));
    },
  });

  const { mutate: postRecommendationsMutate } = postRecommendations;

  const errorMessages = useMemo(() => {
    if (!postRecommendations.isError) {
      return undefined;
    }

    if (postRecommendations.error.response?.status === 406) {
      return t('form.pipelineRecommendations.error.alreadyInPipeline');
    }

    if (postRecommendations.error.response?.status === 400) {
      return t('form.pipelineRecommendations.error.offLimits');
    }

    return t('error.generic');
  }, [postRecommendations.isError, postRecommendations.error, t]);

  const onDraftSubmit = useCallback(() => {
    const formValues = methods.getValues();
    const candidates = getSelectedCandidates();
    return postRecommendationsMutate({
      path: {
        team_uuid: teamId,
        pipeline_uuid: pipelineId,
      },
      body: {
        draft_mode: RecommendationDraftMode.SAVED,
        skip_notifications: true,
        candidates: candidates.map((candidate) => ({
          recommendation_uuid: candidate.recommendation?.uuid,
          candidate_uuid: candidate.uuid,
          introduction_template_uuid:
            formValues.candidates[candidate.uuid]?.about_uuid || undefined,
          highlight_selections:
            formValues.candidates[candidate.uuid]?.highlights_with_selected,
          tailored_highlights: tailoredHighlightsPayload(
            formValues.candidates[candidate.uuid]?.tailored_highlights,
          ),
          intro_email_type:
            formValues.candidates[candidate.uuid]?.intro_email_type,
        })),
      },
    });
  }, [
    teamId,
    pipelineId,
    getSelectedCandidates,
    methods,
    postRecommendationsMutate,
  ]);

  const onSubmit = methods.handleSubmit(
    (formValues) => {
      postRecommendations.mutate({
        path: {
          team_uuid: teamId,
          pipeline_uuid: pipelineId,
        },
        body: {
          draft_mode: undefined,
          skip_notifications: formValues.skip_notifications,
          candidates: candidates.map((candidate) => ({
            recommendation_uuid: candidate.recommendation?.uuid,
            candidate_uuid: candidate.uuid,
            introduction_template_uuid:
              formValues.candidates[candidate.uuid]?.about_uuid || undefined,
            highlight_selections:
              formValues.candidates[candidate.uuid]?.highlights_with_selected,
            tailored_highlights: tailoredHighlightsPayload(
              formValues.candidates[candidate.uuid]?.tailored_highlights,
            ),
            intro_email_type:
              formValues.candidates[candidate.uuid]?.intro_email_type,
          })),
        },
      });
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.warn('error in handleSubmit', error);
    },
  );

  const { watch } = methods;
  // Calculate the validation status for a candidate
  const getValidationStatus = useCallback(
    (candidateUuid: string) => {
      const candidate = candidates.find((c) => c.uuid === candidateUuid);
      const values = watch(`candidates.${candidateUuid}`);
      const hasHighlights =
        Object.values(values?.highlights_with_selected || {}).some(Boolean) ||
        Boolean(values?.tailored_highlights?.some((h) => h.is_selected));
      const isDuplicate =
        candidate?.recommendation?.uuid &&
        (candidate?.pipeline_status === CandidatePipelineStatus.PUBLISHED ||
          candidate?.pipeline_status === CandidatePipelineStatus.SCHEDULED);
      const isOffLimits = Boolean(candidate?.pipeline_off_limits);

      if (isDuplicate) {
        return 'Duplicate';
      }
      if (isOffLimits) {
        return 'Off limits';
      }

      if (!values) {
        return 'Needs review';
      }

      if (!values.about_uuid && !hasHighlights) {
        return 'Add narratives';
      }

      if (!values.about_uuid) {
        return 'No about';
      }

      if (!hasHighlights) {
        return 'No highlights';
      }

      return 'complete';
    },
    [candidates, watch],
  );

  return (
    <FormProvider {...methods}>
      <div className="flex flex-1 flex-col overflow-hidden p-6">
        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Content header */}
          <div className="bg-gray-50">
            <h2 className="mb-2 text-lg font-semibold text-gray-800">
              Edit narratives & introduction preferences
            </h2>
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600">
                {candidates.length} executives
              </p>
            </div>
          </div>

          {overdueNames.length > 0 && (
            <InvoiceOverdueBanner
              // Match the visual width of the two panes below:
              // 320px list + 8px gap + 500px panel.
              className="mb-4 max-w-[828px]"
              severity={overdueSeverity}
            >
              {t('invoiceStatus.selectedOverdue', {
                names: overdueNames.join(', '),
              })}
            </InvoiceOverdueBanner>
          )}

          {/* Content sections */}
          <div className="flex flex-1 gap-2 overflow-hidden">
            {/* Left panel - Candidate list */}
            <div className="w-80 w-[320px] overflow-y-auto bg-gray-50">
              <div className="space-y-2">
                {candidates.length ? (
                  candidates.map((candidate) => (
                    <div
                      key={candidate.uuid}
                      className={cn('group hover:border-blue-600', {
                        'border-blue-600':
                          candidate.uuid === selectedCandidate?.uuid,
                      })}
                    >
                      <RecommendationListItem
                        candidate={candidate}
                        status={getValidationStatus(candidate.uuid)}
                        selected={candidate.uuid === selectedCandidate?.uuid}
                        onClick={() => onCandidateClick(candidate)}
                      />
                    </div>
                  ))
                ) : (
                  <ListPlaceholderLoading className="animate-none" />
                )}
              </div>
            </div>

            {/* Right panel - Form */}
            {selectedCandidate && (
              <RecommendationPanel
                key={selectedCandidate?.uuid}
                candidate={selectedCandidate}
              />
            )}

            {candidates.length === 0 && (
              <div className="max-w-[500px] flex-1 overflow-y-auto bg-gray-50">
                <div className="flex h-full flex-col items-center justify-center gap-2 overflow-y-auto rounded-xl bg-white p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 [&>svg]:h-5 [&>svg]:w-5">
                    <IconHeartHandshake />
                  </span>
                  <p className="text-heading-200 text-gray-700">
                    There are no executives to display
                  </p>

                  <Button
                    onClick={() => {
                      onPreviousStep();
                    }}
                    size={40}
                    variant="default-link"
                  >
                    Submit executives
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 bg-white py-2.5">
        <div className="flex gap-2.5 px-6">
          <Button
            type="button"
            onClick={onDraftSubmit}
            size={32}
            variant="default-tertiary"
            disabled={postRecommendations.isPending}
          >
            Save as draft
          </Button>
          <Button
            size={32}
            variant="default-primary"
            type="button"
            disabled={candidateUuids.some((uuid) => {
              const status = getValidationStatus(uuid);
              return status === 'Duplicate' || status === 'Off limits';
            })}
            onClick={() => setIsPublishDialogOpen(true)}
          >
            Submit to external pipeline
          </Button>
        </div>
      </div>
      <Dialog
        actionButtonProps={{
          children: t('form.trending.publish.publish'),
          variant: 'default-primary',
          isLoading: postRecommendations.isPending,
        }}
        error={errorMessages}
        heading={t('form.pipelineRecommendations.title')}
        onClose={() => setIsPublishDialogOpen(false)}
        onSubmit={onSubmit}
        open={isPublishDialogOpen}
      >
        <div className="text-body-100 flex flex-col gap-2 px-8 font-normal text-gray-600">
          <span>
            <Trans
              i18nKey="form.pipelineRecommendations.confirmRecommendations"
              values={{
                recommendations_count: candidates.length,
                team_name: queryPipeline.data?.team.name,
                pipeline_name: queryPipeline.data?.name,
              }}
            >
              <span className="text-body-100 text-gray-800"></span>
            </Trans>
          </span>
          <div className="flex flex-col">
            <div className="flex items-center gap-x-2">
              <FormCheckbox
                name="skip_notifications"
                size={20}
                checked={skipNotifications}
                onChange={(checked) => setSkipNotifications(checked)}
              >
                {t(
                  'form.pipelineRecommendations.field.skip_notifications.label',
                )}
              </FormCheckbox>
            </div>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
